import { Link } from '@tanstack/react-router'
import type { AdminUserResponseDto } from '@lunary/api-client'
import { LunarAmount } from '@lunary/ui-web/lunar-text'
import { userLoginMethods, userTitle } from '../users/identity'
import { LOGIN_METHOD_LABELS, formatDate, formatDateTime, relativeSeen, sourceLabel } from '../users/labels'
import styles from './person-card.module.css'

type Props = {
  person: AdminUserResponseDto
  readings?: number
  done?: number
}

const TELEGRAM = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21.5 3.5 2.8 10.7c-1 .4-1 1.5 0 1.8l4.6 1.4 1.8 5.5c.2.7 1.1.9 1.6.4l2.6-2.4 4.8 3.5c.6.4 1.4.1 1.6-.6L22.9 5c.3-1.1-.6-1.9-1.4-1.5ZM9.6 14.3l-.4 3.6-1.2-4 9.6-6.2-8 6.6Z" />
  </svg>
)

const MARKS: Readonly<Record<string, string>> = { yandex: 'Я', google: 'G', vk: 'VK' }

function MethodIcon({ method }: { method: string }) {
  return (
    <span className={styles.icon} data-method={method} aria-hidden="true">
      {method === 'telegram' ? TELEGRAM : (MARKS[method] ?? '@')}
    </span>
  )
}

function signupMethod(person: AdminUserResponseDto, methods: readonly string[]): string | null {
  if (person.signup_source === 'telegram_bot' || person.signup_source === 'telegram_mini_app') return 'telegram'
  return methods.find((m) => m !== 'telegram') ?? methods[0] ?? null
}

function daysSince(iso: string): number {
  return Math.max(1, Math.ceil((Date.now() - new Date(iso).getTime()) / 86_400_000))
}

export function PersonCard({ person, readings, done }: Props) {
  const { title } = userTitle(person)
  const methods = userLoginMethods(person)
  const badge = signupMethod(person, methods)
  const named = Boolean(person.display_name)
  const handle = person.telegram_username
  const emails = [person.email, ...person.oauth_emails].filter((e): e is string => Boolean(e))
  const shownTitle = named || title !== handle ? title : `@${handle}`

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.avatar} data-empty={!named || undefined} aria-hidden="true">
          {person.avatar_url ? (
            <img className={styles.photo} src={person.avatar_url} alt="" width={64} height={64} />
          ) : named ? (
            (person.display_name?.trim().charAt(0).toUpperCase() ?? '?')
          ) : (
            '?'
          )}
          {badge && <MethodIcon method={badge} />}
        </span>
        <div className={styles.name}>{shownTitle}</div>
        {handle ? (
          <a className={styles.handle} href={`https://t.me/${handle}`} target="_blank" rel="noreferrer">
            @{handle}
          </a>
        ) : person.telegram_linked ? (
          <span className={styles.muted}>Telegram без ника</span>
        ) : emails[0] && emails[0] !== shownTitle ? (
          <span className={styles.muted}>{emails[0]}</span>
        ) : null}
        {methods.length > 0 && (
          <ul className={styles.methods} aria-label="Способы входа">
            {methods.map((m) => (
              <li key={m}>
                <MethodIcon method={m} />
                {LOGIN_METHOD_LABELS[m] ?? m}
              </li>
            ))}
          </ul>
        )}
        {person.blocked_at && <span className="badge danger">Заблокирован</span>}
      </div>
      {readings !== undefined && done !== undefined && (
        <dl className={styles.stats}>
          <div>
            <dt>разборов</dt>
            <dd>{readings}</dd>
          </div>
          <div>
            <dt>дней с нами</dt>
            <dd>{daysSince(person.created_at)}</dd>
          </div>
          <div>
            <dt>готово</dt>
            <dd>{done}</dd>
          </div>
        </dl>
      )}
      <dl className={styles.details}>
        <dt>Пришёл из</dt>
        <dd>{sourceLabel(person.signup_source)}</dd>
        <dt>Был в сети</dt>
        <dd title={formatDateTime(person.last_seen_at)}>
          {relativeSeen(person.last_seen_at)}
          {person.last_seen_source && ` · ${sourceLabel(person.last_seen_source)}`}
        </dd>
        <dt>С нами с</dt>
        <dd>{formatDate(person.created_at)}</dd>
        <dt>Лунары</dt>
        <dd>
          <LunarAmount n={person.lunars_balance} size={16} word={false} />
        </dd>
        {emails.map((e) => (
          <PersonEmail key={e} email={e} />
        ))}
      </dl>
      <div className={styles.actions}>
        <Link to="/users/$userId" params={{ userId: person.id }} className={styles.action}>
          Открыть в «Пользователях»
        </Link>
        {handle && (
          <a className={styles.action} href={`https://t.me/${handle}`} target="_blank" rel="noreferrer">
            Написать в Telegram
          </a>
        )}
      </div>
    </div>
  )
}

function PersonEmail({ email }: { email: string }) {
  return (
    <>
      <dt>Почта</dt>
      <dd>{email}</dd>
    </>
  )
}
